import { FetchResult } from '@apollo/client';
import get from 'lodash/get';

import { ColumnFilterItem } from '@components/MultipleFilterDropdown';
import { CheckoutInternalOrdersMutation, OrderItem } from '@hooks/checkout';
import { NotificationTypeEnum } from '@components/NotificationCenter/utils';

/**
 * This one contains all of custom events
 */

export const firePurchaseEvent = (
  mutationResult: FetchResult<CheckoutInternalOrdersMutation, Record<string, any>, Record<string, any>>,
) => {
  const data = get(mutationResult, 'data.checkoutWithInternalDispatch');

  if (!data) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });

  for (const el of data) {
    if (el.status !== 'draft') continue;

    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: el?.id,
        value: Number(el?.total),
        currency: 'VND',
        items: el?.orderItems?.nodes?.map((item: OrderItem) => ({
          item_id: item?.variant.id,
          item_name: item?.variant.name,
          item_variant: item?.variant.materialNumber,
          price: Number(item?.price),
          quantity: item?.quantity,
        })),
      },
    });
  }
};

export enum exportType {
  INDIVIDUAL = 'xuất hàng ngay',
  GROUP = 'xuất cùng lúc',
}
export const fireExportGiftEvent = (items: Record<string, any>[], exportType: exportType, campaignIds: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'export_gift',
    export_type: exportType,
    campaign_ids: campaignIds,
    ecommerce: {
      items: items.map(item => ({
        item_id: item?.id,
        item_name: item?.name,
        item_variant: item?.materialNumber,
        quantity: item?.quantity,
      })),
    },
  });
};

export const fireAddToCartEvent = (items: Record<string, any>[], addFrom: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    add_to_cart_from: addFrom,
    ecommerce: {
      items,
    },
  });
};

export const fireVisitDiscountsEvent = (visitDiscountsFrom: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'visit_discounts',
    visitDiscountsFrom,
  });
};

export const fireVisitCampaignsEvent = (visitCampaignsFrom: string, userName: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'visit_campaigns',
    by_user: userName,
    visitCampaignsFrom,
  });
};

export const fireFilterEvent = (filterValuesName: string, filterFrom: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'filter',
    filterFrom,
    filterValues: filterValuesName,
  });
};

export const fireSearchEvent = (searchTerm: string, searchFrom: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'search',
    search_term: searchTerm,
    search_from: searchFrom,
  });
};

export const fireClickBannerEvent = (url: string, position: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'click_banner',
    banner_url: url,
    banner_position: position,
  });
};

export const fireVisitProductsEvent = (from: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'visit_products',
    visit_products_from: from,
  });
};

export const fireClickProductsCarouselEvent = (carouselType: string, materialNumber: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'click_carousel',
    carousel_type: carouselType,
    material_number: materialNumber,
  });
};

export const fireDisallowNotificationEvent = (userName = 'unknown') => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'disallow_notification',
    by_user: userName,
  });
};

export const fireViewNotificationsEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'view_notifications',
  });
};

export const fireViewItemEvent = (item: Record<string, any>) => {
  const { id, name, materialNumber, vatWholesalePrice } = item;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      items: [
        {
          item_id: id,
          item_name: name,
          item_variant: materialNumber,
          currency: 'VND',
          price: vatWholesalePrice,
        },
      ],
    },
  });
};

export const fireViewItemListEvent = (items: (Record<string, any> | null | undefined)[]) => {
  if (items.some(item => !item)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item_list',
    ecommerce: {
      items: [
        ...items.map(item => ({
          item_id: item?.id,
          item_name: item?.name,
          item_variant: item?.materialNumber,
          currency: 'VND',
          price: item?.vatWholesalePrice,
        })),
      ],
    },
  });
};

const notificationTypeMap: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.UserOrderStatus]: 'Trạng thái đơn hàng',
  [NotificationTypeEnum.UserRevertCancellation]: 'Trạng thái đơn hàng',
  [NotificationTypeEnum.UserPromotionAlert]: 'Chương trình khuyến mãi',
};

export const fireViewProductGuaranteeEvent = (userName: string, materialNumber: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `view_product_guarantee`,
    by_user: userName,
    material_number: materialNumber,
  });
};

export const fireClickNotificationEvent = (clickFrom = 'web-push', notificationType: NotificationTypeEnum) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'notification_click',
    click_from: clickFrom,
    notification_type: notificationTypeMap[notificationType],
  });
};

export const fireWebTourBeginEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'web_tour_begin',
  });
};

export const fireWebTourCompleteEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'web_tour_complete',
  });
};

export const fireLoginEvent = (method = 'default') => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'login',
    method,
  });
};

export const fireInventoryCheckEvent = (variantId: string) => {
  // Check general information: "Du hang", "Can dieu phoi"
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'inventory_check',
    variant_id: variantId,
  });
};

export const fireInventoryDetailCheckEvent = (variantId: string) => {
  // Check information in detail: "Thoi gian dieu phoi", ...
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'inventory_detail_check',
    variant_id: variantId,
  });
};

export const fireCustomPageViewEvent = (pageViewParams: { pageUrl: string; pageTitle: string }) => {
  const { pageUrl, pageTitle } = pageViewParams;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `custom_page_view`,
    page_url: pageUrl,
    page_title: pageTitle,
  });
};

export const fireClickPromotionPopupEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `promotion_popup_click`,
  });
};

export const fireClosePromotionPopupEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `promotion_popup_close`,
  });
};

export const fireQuotationSearchEvent = (searchTerm: string, userName: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'quotation_search',
    search_term: searchTerm,
    by_user: userName,
  });
};

export const fireEvent = (event: string, userName: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: event,
    by_user: userName,
  });
};

export const fireEventWithProduct = (event: string, userName: string, product: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: event,
    by_user: userName,
    material_number: product,
  });
};

export const fireViewProductDescriptionEvent = (userName: string, materialNumber: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: `view_product_description`,
    by_user: userName,
    material_number: materialNumber,
  });
};

export const fireClickZaloWidgetEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'click_zalo_widget',
  });
};

/**
 * This one contains all utility functions
 */
interface FilterItem {
  text: string;
  value: string;
}
export const getFilterValuesName = (
  filterValues: Record<string, string[]>,
  categories: ColumnFilterItem[],
  brands: FilterItem[],
  colors: FilterItem[],
  attributes: FilterItem[],
) => {
  const categoryNames: string[] = [];
  const brandNames: string[] = [];
  const colorNames: string[] = [];
  const attributeNames: string[] = [];

  const categoryFilterValues = filterValues.categoryIds;
  const brandFilterValues = filterValues.brandIds;
  const colorFilterValues = filterValues.optionValueIds;
  const attributeFilterValues = filterValues.attributes;

  const flatCategories: ColumnFilterItem[] = [];
  function flattenCategory(categories: ColumnFilterItem[]) {
    if (!categories) return;

    categories.forEach(category => {
      if (category.children) {
        flattenCategory(category.children);
      }
      flatCategories.push(category);
    });
  }
  flattenCategory(categories);

  flatCategories.forEach(category => {
    if (categoryFilterValues.includes(category.value as string)) categoryNames.push(category.text as string);
  });
  brands.forEach(brand => {
    if (brandFilterValues.includes(brand.value)) brandNames.push(brand.text);
  });
  colors.forEach(color => {
    if (colorFilterValues.includes(color.value)) colorNames.push(color.text);
  });
  attributes.forEach(attr => {
    if (attributeFilterValues.includes(attr.value)) attributeNames.push(attr.text);
  });

  return categoryNames.concat(brandNames).concat(colorNames).concat(attributeNames).join('-');
};
